import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import Select from 'react-select'
import Layout from '../../Component/Layout'
import { PURCHASE_API } from '../../apiConfig'
// All Inventory Dashboard API calls go through PURCHASE_API — this screen's
// backend lives entirely under PURCHASE_API/inventory (InventoryDashboard.routes/
// controller/service.js).

// ---------------------------------------------------------------------------
// Inline SVG icons (no external icon library needed — avoids the
// "Can't resolve 'lucide-react'" build error). Keys are PascalCase so
// `<Icon.ChevronRight />` satisfies eslint's react/jsx-pascal-case rule.
// ---------------------------------------------------------------------------

const Icon = {
    Box: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
            <path d="m3.3 7 8.7 5 8.7-5M12 22V12" />
        </svg>
    ),
    Download: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M12 3v12m0 0 4-4m-4 4-4-4M4 18v1a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-1" />
        </svg>
    ),
    Swap: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m17 2 4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 22l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
    ),
    Recycle: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M7 19H4.815a1.83 1.83 0 0 1-1.57-.881 1.785 1.785 0 0 1-.004-1.784L7.196 9.5M11 19h8.203a1.83 1.83 0 0 0 1.556-.89 1.784 1.784 0 0 0 0-1.775l-1.226-2.12M14.5 4.5l-3.257 5.642a2 2 0 0 1-3.464-2L11 2.5M17.5 12l3.257 5.642a2 2 0 0 1-1.732 3H14" />
        </svg>
    ),
    Refresh: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16m0-4a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M3 16v5h5" />
        </svg>
    ),
    Export: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M12 15V3m0 12-4-4m4 4 4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
        </svg>
    ),
    ChevronDown: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m6 9 6 6 6-6" />
        </svg>
    ),
    ChevronRight: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m9 18 6-6-6-6" />
        </svg>
    ),
    Calendar: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
    ),
    List: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
    ),
    Info: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
        </svg>
    ),
    Alert: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4M12 17h.01" />
        </svg>
    ),
    ArrowRight: (p) => (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}>
            <path d="M5 12h14m-7-7 7 7-7 7" />
        </svg>
    ),
}

// ---------------------------------------------------------------------------
// materialCategories now come from the backend
// (GET /purchase/inventory/material-category via PURCHASE_API).
//
// dataByCategory (KPIs / stage flow / ageing) below this point is still
// mock data; that's the next migration step, tracked via the
// GET /purchase/inventory/dashboard endpoint already stubbed on the backend.
// ---------------------------------------------------------------------------

// Turns "520,480 kg" + a ₹/kg rate into a formatted "₹40.86 Cr" / "₹12.5 L" string
function costFromKg(kgString, ratePerKg) {
    const kg = parseFloat(kgString.replace(/,/g, ''))
    const value = kg * ratePerKg
    if (value >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`
    if (value >= 1e5) return `₹${(value / 1e5).toFixed(2)} L`
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

// Same Cr/L formatting as costFromKg, but for a value that's already summed
// server-side (no kg * rate multiplication needed here).
function formatCurrencyValue(value) {
    if (!value) return '₹0'
    if (value >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`
    if (value >= 1e5) return `₹${(value / 1e5).toFixed(2)} L`
    return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
}

// Given the selected [dateFrom, dateTo] window, returns the immediately
// preceding window of the same length — used to compute "vs previous period" trends.
function shiftRangeBack(dateFrom, dateTo) {
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    const days = Math.round((to - from) / 86400000) + 1
    const prevTo = new Date(from)
    prevTo.setDate(prevTo.getDate() - 1)
    const prevFrom = new Date(prevTo)
    prevFrom.setDate(prevFrom.getDate() - (days - 1))
    return { prevFrom: prevFrom.toISOString().slice(0, 10), prevTo: prevTo.toISOString().slice(0, 10) }
}

// current vs previous -> { dir: 'up' | 'down', value: '4.3% vs previous period' }
function calcTrend(current, previous) {
    if (previous === null || previous === undefined) return null
    if (previous === 0) {
        if (current === 0) return null // no change, nothing meaningful to show
        // previous period had zero — any current value is a 100%+ increase.
        // % change is undefined/infinite in this case, so label it as new activity.
        return { dir: 'up', value: 'New vs previous period' }
    }
    const pct = ((current - previous) / Math.abs(previous)) * 100
    return { dir: pct >= 0 ? 'up' : 'down', value: `${Math.abs(pct).toFixed(1)}% vs previous period` }
}

const dataByCategory = {
    Copper: {
        kpis: [
            { label: 'Opening Balance', kg: '520,480 kg', km: '125,640 km', icon: 'Box', iconBg: '#DCEAFE', iconColor: '#2563EB' },
            { label: 'Total Received', kg: '1,250,360 kg', km: '301,850 km', icon: 'Download', iconBg: '#DBEAFE', iconColor: '#3B82F6', trend: { dir: 'up', value: "8.2% vs Apr '25" } },
            { label: 'Issued to Production', kg: '1,180,240 kg', km: '285,320 km', icon: 'Swap', iconBg: '#EDE9FE', iconColor: '#7C3AED', trend: { dir: 'up', value: "6.5% vs Apr '25" } },
            { label: 'On Hand Balance', kg: '590,600 kg', km: '142,170 km', icon: 'Box', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'down', value: "4.3% vs Apr '25" } },
            { label: 'Scrap & Rework', kg: '14,320 kg', km: '3,480 km', icon: 'Recycle', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'up', value: "12.6% vs Apr '25" } },
        ],
        stages: [
            { name: 'Raw Material', sub: '(RBD)', color: '#FB923C', in: '520,480 kg', inSub: '125,640 km', out: '501,240 kg', outSub: '121,820 km', bal: '19,240 kg', balSub: '3,820 km' },
            { name: 'Intermediate', sub: '', color: '#94A3B8', in: '501,240 kg', inSub: '121,820 km', out: '468,350 kg', outSub: '113,690 km', bal: '32,890 kg', balSub: '8,130 km' },
            { name: 'Tin Electro', sub: 'Plating', color: '#94A3B8', in: '468,350 kg', inSub: '113,690 km', out: '452,180 kg', outSub: '109,760 km', bal: '16,170 kg', balSub: '3,930 km' },
            { name: 'Fine Wire', sub: 'Drawing', color: '#FBBF24', in: '452,180 kg', inSub: '109,760 km', out: '428,550 kg', outSub: '104,020 km', bal: '23,630 kg', balSub: '5,740 km' },
            { name: 'Stranding', sub: '', color: '#34D399', in: '428,550 kg', inSub: '104,020 km', out: '401,820 kg', outSub: '97,540 km', bal: '26,730 kg', balSub: '6,480 km' },
            { name: 'Insulation', sub: '', color: '#FB923C', in: '401,820 kg', inSub: '97,540 km', out: '372,120 kg', outSub: '90,320 km', bal: '29,700 kg', balSub: '7,220 km' },
            { name: 'Braiding /', sub: 'Screening', color: '#A78BFA', in: '372,120 kg', inSub: '90,320 km', out: '346,850 kg', outSub: '84,180 km', bal: '25,270 kg', balSub: '6,140 km' },
            { name: 'Armouring', sub: '', color: '#FB7185', in: '346,850 kg', inSub: '84,180 km', out: '318,640 kg', outSub: '77,320 km', bal: '28,210 kg', balSub: '6,880 km' },
            { name: 'Outer Sheathing', sub: '', color: '#64748B', in: '318,640 kg', inSub: '77,320 km', out: '298,340 kg', outSub: '72,140 km', bal: '20,300 kg', balSub: '5,180 km' },
            { name: 'Finished Goods', sub: '', color: '#F59E0B', in: '298,340 kg', inSub: '72,140 km', out: '283,560 kg', outSub: '68,480 km', bal: '14,780 kg', balSub: '3,660 km' },
        ],
        ageingBuckets: [
            { label: '0 – 30 days', kg: '95,320', pct: 45.3, color: '#10B981' },
            { label: '31 – 60 days', kg: '62,140', pct: 29.5, color: '#3B82F6' },
            { label: '61 – 90 days', kg: '34,760', pct: 16.5, color: '#F59E0B' },
            { label: '> 90 days', kg: '18,380', pct: 8.7, color: '#EF4444' },
        ],
        ageingNote: '8.7% (18,380 kg) of Copper is older than 90 days',
    },
    PVC: {
        kpis: [
            { label: 'Opening Balance', kg: '186,200 kg', km: '46,900 km', icon: 'Box', iconBg: '#DCEAFE', iconColor: '#2563EB' },
            { label: 'Total Received', kg: '412,680 kg', km: '98,450 km', icon: 'Download', iconBg: '#DBEAFE', iconColor: '#3B82F6', trend: { dir: 'up', value: "5.4% vs Apr '25" } },
            { label: 'Issued to Production', kg: '389,540 kg', km: '92,110 km', icon: 'Swap', iconBg: '#EDE9FE', iconColor: '#7C3AED', trend: { dir: 'up', value: "3.9% vs Apr '25" } },
            { label: 'On Hand Balance', kg: '176,840 kg', km: '41,760 km', icon: 'Box', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'up', value: "2.1% vs Apr '25" } },
            { label: 'Scrap & Rework', kg: '5,120 kg', km: '1,240 km', icon: 'Recycle', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'down', value: "1.8% vs Apr '25" } },
        ],
        stages: [
            { name: 'Raw Material', sub: '(Resin)', color: '#22C55E', in: '186,200 kg', inSub: '46,900 km', out: '178,340 kg', outSub: '44,980 km', bal: '7,860 kg', balSub: '1,920 km' },
            { name: 'Compounding', sub: '', color: '#94A3B8', in: '178,340 kg', inSub: '44,980 km', out: '169,520 kg', outSub: '42,610 km', bal: '8,820 kg', balSub: '2,370 km' },
            { name: 'Insulation', sub: '', color: '#FB923C', in: '169,520 kg', inSub: '42,610 km', out: '158,960 kg', outSub: '39,880 km', bal: '10,560 kg', balSub: '2,730 km' },
            { name: 'Sheathing', sub: '', color: '#64748B', in: '158,960 kg', inSub: '39,880 km', out: '148,210 kg', outSub: '36,940 km', bal: '10,750 kg', balSub: '2,940 km' },
            { name: 'Extrusion', sub: 'QC', color: '#FBBF24', in: '148,210 kg', inSub: '36,940 km', out: '139,480 kg', outSub: '34,520 km', bal: '8,730 kg', balSub: '2,420 km' },
            { name: 'Finished Goods', sub: '', color: '#F59E0B', in: '139,480 kg', inSub: '34,520 km', out: '128,900 kg', outSub: '31,760 km', bal: '10,580 kg', balSub: '2,760 km' },
        ],
        ageingBuckets: [
            { label: '0 – 30 days', kg: '38,650', pct: 52.1, color: '#10B981' },
            { label: '31 – 60 days', kg: '19,220', pct: 25.9, color: '#3B82F6' },
            { label: '61 – 90 days', kg: '11,340', pct: 15.3, color: '#F59E0B' },
            { label: '> 90 days', kg: '4,890', pct: 6.7, color: '#EF4444' },
        ],
        ageingNote: '6.7% (4,890 kg) of PVC is older than 90 days',
    },
    'Master Batch': {
        kpis: [
            { label: 'Opening Balance', kg: '64,300 kg', km: '15,820 km', icon: 'Box', iconBg: '#DCEAFE', iconColor: '#2563EB' },
            { label: 'Total Received', kg: '128,560 kg', km: '31,240 km', icon: 'Download', iconBg: '#DBEAFE', iconColor: '#3B82F6', trend: { dir: 'up', value: "4.1% vs Apr '25" } },
            { label: 'Issued to Production', kg: '121,940 kg', km: '29,580 km', icon: 'Swap', iconBg: '#EDE9FE', iconColor: '#7C3AED', trend: { dir: 'up', value: "2.7% vs Apr '25" } },
            { label: 'On Hand Balance', kg: '52,180 kg', km: '12,640 km', icon: 'Box', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'down', value: "1.4% vs Apr '25" } },
            { label: 'Scrap & Rework', kg: '1,860 kg', km: '450 km', icon: 'Recycle', iconBg: '#D1FAE5', iconColor: '#059669', trend: { dir: 'up', value: "3.3% vs Apr '25" } },
        ],
        stages: [
            { name: 'Raw Material', sub: '(Pigment)', color: '#8B5CF6', in: '64,300 kg', inSub: '15,820 km', out: '61,120 kg', outSub: '14,960 km', bal: '3,180 kg', balSub: '860 km' },
            { name: 'Blending', sub: '', color: '#94A3B8', in: '61,120 kg', inSub: '14,960 km', out: '57,840 kg', outSub: '14,020 km', bal: '3,280 kg', balSub: '940 km' },
            { name: 'Compounding', sub: '', color: '#FBBF24', in: '57,840 kg', inSub: '14,020 km', out: '53,960 kg', outSub: '12,980 km', bal: '3,880 kg', balSub: '1,040 km' },
            { name: 'Quality Check', sub: '', color: '#FB923C', in: '53,960 kg', inSub: '12,980 km', out: '50,120 kg', outSub: '11,940 km', bal: '3,840 kg', balSub: '1,040 km' },
            { name: 'Finished Goods', sub: '', color: '#F59E0B', in: '50,120 kg', inSub: '11,940 km', out: '46,380 kg', outSub: '10,920 km', bal: '3,740 kg', balSub: '1,020 km' },
        ],
        ageingBuckets: [
            { label: '0 – 30 days', kg: '24,180', pct: 46.3, color: '#10B981' },
            { label: '31 – 60 days', kg: '14,260', pct: 27.3, color: '#3B82F6' },
            { label: '61 – 90 days', kg: '8,420', pct: 16.1, color: '#F59E0B' },
            { label: '> 90 days', kg: '5,320', pct: 10.3, color: '#EF4444' },
        ],
        ageingNote: '10.3% (5,320 kg) of Master Batch is older than 90 days',
    },
}

// Donut slice colors, cycled by index — backend has no notion of a "type color".
const TYPE_COLORS = ['#F97316', '#2563EB', '#16A34A', '#7C3AED', '#FACC15', '#94A3B8', '#EF4444', '#0EA5E9']

const alerts = [
    { level: 'High', msg: 'Copper inventory above maximum level', qty: '45,320', date: 'May 31', ratePerKg: 785 },
    { level: 'High', msg: 'WIP in Insulation > 7 days', qty: '18,640', date: 'May 30', ratePerKg: 340 },
    { level: 'Medium', msg: 'Scrap exceeds standard by 2.1%', qty: '3,280', date: 'May 28', ratePerKg: 210 },
    { level: 'Medium', msg: 'Inventory ageing > 60 days', qty: '12,450', date: 'May 27', ratePerKg: 265 },
    { level: 'Low', msg: 'FG dispatch pending > 3 days', qty: '8,540', date: 'May 26', ratePerKg: 420 },
]

const levelStyles = {
    High: { bg: '#FEE2E2', color: '#DC2626' },
    Medium: { bg: '#FEF3C7', color: '#D97706' },
    Low: { bg: '#DBEAFE', color: '#2563EB' },
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function KpiCard({ item, ratePerKg }) {
    const IconCmp = Icon[item.icon]
    const cost = costFromKg(item.kg, ratePerKg)
    return (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: item.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconCmp width={18} height={18} style={{ color: item.iconColor }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 12, color: '#64748B', margin: 0, whiteSpace: 'nowrap' }}>{item.label}</p>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#0F172A', margin: '2px 0 0', lineHeight: 1.2 }}>{item.kg}</p>
                <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, lineHeight: 1.3 }}>{item.km}</p>
                <p style={{ fontSize: 12.5, color: '#0F766E', fontWeight: 600, margin: '3px 0 0', lineHeight: 1.3 }}>{cost}</p>
                {item.trend && (
                    <p style={{ fontSize: 12, margin: '4px 0 0', color: item.trend.dir === 'up' ? '#059669' : '#DC2626', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span>{item.trend.dir === 'up' ? '▲' : '▼'}</span>
                        {item.trend.value}
                    </p>
                )}
            </div>
        </div>
    )
}

function LiveMetricCard({ label, icon, iconBg, iconColor, data, loading, trend }) {
    const IconCmp = Icon[icon]
    const qtyDisplay = loading
        ? '…'
        : data.uom
        ? `${(data.qty || 0).toLocaleString('en-IN')} ${data.uom}`
        : (data.qty || 0).toLocaleString('en-IN')
    return (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconCmp width={18} height={18} style={{ color: iconColor }} />
            </div>
            <div style={{ minWidth: 0 }}>
                <p style={{ fontSize: 12, color: '#64748B', margin: 0, whiteSpace: 'nowrap' }}>{label}</p>
                <p style={{ fontSize: 18, fontWeight: 600, color: '#0F172A', margin: '2px 0 0', lineHeight: 1.2 }}>{qtyDisplay}</p>
                {!loading && data.uomMixed && (
                    <p style={{ fontSize: 11, color: '#C2410C', margin: '2px 0 0' }}>Mixed UOM across products</p>
                )}
                <p style={{ fontSize: 12.5, color: '#0F766E', fontWeight: 600, margin: '3px 0 0', lineHeight: 1.3 }}>
                    {loading ? '' : formatCurrencyValue(data.cost)}
                </p>
                {!loading && trend && (
                    <p style={{ fontSize: 12, margin: '4px 0 0', color: trend.dir === 'up' ? '#059669' : '#DC2626', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <span>{trend.dir === 'up' ? '▲' : '▼'}</span>
                        {trend.value}
                    </p>
                )}
            </div>
        </div>
    )
}


// Border colors cycle through this palette since the backend has no notion
// of a "stage color" — workcenter_name is just a string.
const STAGE_COLORS = ['#FB923C', '#94A3B8', '#FBBF24', '#34D399', '#A78BFA', '#FB7185', '#64748B', '#F59E0B', '#22C55E', '#8B5CF6']

// Fixed colors for the ageing buckets, in the same 0–30/31–60/61–90/>90 order
// the backend always returns them in.
const AGEING_BUCKET_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444']

// Formats a plain qty number with the category's uom suffix, e.g. "12,450 kg".
// uom is null when the category mixes uoms (uomMixed) — shown without a unit.
function formatStageQty(qty, uom) {
    const n = (qty || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
    return uom ? `${n} ${uom}` : n
}

function StageCard({ stage, isLast, color, uom }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <div style={{ width: 190, background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', borderTop: `4px solid ${color}`, padding: 12 }}>
                <p style={{ fontSize: 13.5, fontWeight: 600, color: '#1E293B', margin: 0, lineHeight: 1.3 }}>
                    {stage.name}
                </p>
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12 }}>
                    <div>
                        <span style={{ color: '#94A3B8' }}>In</span>
                        <div style={{ color: '#334155', fontWeight: 500 }}>{formatStageQty(stage.in, uom)}</div>
                        <div style={{ color: '#0F766E', fontWeight: 600 }}>{formatCurrencyValue(stage.inCost)}</div>
                    </div>
                    <div>
                        <span style={{ color: '#94A3B8' }}>Out</span>
                        <div style={{ color: '#334155', fontWeight: 500 }}>{formatStageQty(stage.out, uom)}</div>
                        <div style={{ color: '#0F766E', fontWeight: 600 }}>{formatCurrencyValue(stage.outCost)}</div>
                    </div>
                    <div>
                        <span style={{ color: '#94A3B8' }}>Bal</span>
                        <div style={{ color: '#0F172A', fontWeight: 700 }}>{formatStageQty(stage.bal, uom)}</div>
                        <div style={{ color: '#0F766E', fontWeight: 700 }}>{formatCurrencyValue(stage.balCost)}</div>
                    </div>
                </div>
            </div>
            {!isLast && (
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 4px', flexShrink: 0 }}>
                    <Icon.ChevronRight width={14} height={14} style={{ color: '#94A3B8' }} />
                </div>
            )}
        </div>
    )
}

// Pure-CSS conic-gradient donut — no chart library required.
// `data` items need { color, pctRaw } where pctRaw is that slice's share (0-100).
function DonutChart({ data, total, totalLabel }) {
    let cumulative = 0
    const stops = data.map((d) => {
        const start = cumulative
        cumulative += d.pctRaw
        return `${d.color} ${start}% ${cumulative}%`
    })
    return (
        <div style={{ width: 160, height: 160, borderRadius: '50%', flexShrink: 0, background: `conic-gradient(${stops.join(', ')})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 4 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', lineHeight: 1.25 }}>{total}</span>
                <span style={{ fontSize: 10.5, color: '#94A3B8', marginTop: 2 }}>{totalLabel}</span>
            </div>
        </div>
    )
}

function FilterField({ label, children }) {
    return (
        <div>
            <label style={{ display: 'block', fontSize: 12, color: '#64748B', marginBottom: 4 }}>{label}</label>
            {children}
        </div>
    )
}



// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function InventoryDashboard() {

    // ── Material Category (from backend, via react-select) ─────────────
    const [materialCategories, setMaterialCategories] = useState([])
    const [category, setCategory] = useState('')
    const [categoryLoading, setCategoryLoading] = useState(true)

    // Plant dropdown is disabled for now (no backend wiring yet), but keep
    // a stable value ('' = all plants) so downstream fetches have something to pass.
    const [plant] = useState('')

    // ── Costing Type (static options for now) ───────────────────────────
    const costingTypeOptions = [
        { value: 'moving_average', label: 'Moving Average' },
        { value: 'latest_grn', label: 'Latest GRN' },
    ]
    const [costingType, setCostingType] = useState('')
    const costingTypeSelectValue = costingTypeOptions.find((o) => o.value === costingType) || null

    // ── Date range (user-selectable, drives future dashboard fetches) ──
    const today = new Date().toISOString().slice(0, 10)
    const [dateFrom, setDateFrom] = useState(today)
    const [dateTo, setDateTo] = useState(today)

    const fetchMaterialCategories = useCallback(async () => {
        setCategoryLoading(true)
        try {
            const res = await axios.get(`${PURCHASE_API}/inventory-dashboard/material-category`)
            const list = res.data || []
            setMaterialCategories(list)
            // Keep current selection if it still exists, otherwise fall back to
            // "Select" (empty) — we no longer auto-pick the first category.
            setCategory((prev) => (list.some((m) => m.name === prev) ? prev : ''))
        } catch (err) {
            console.error('Failed to fetch material categories:', err)
        } finally {
            setCategoryLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchMaterialCategories()
    }, [fetchMaterialCategories])

    // "Refresh" reloads the whole screen, same as hitting the browser refresh.
    const handleRefresh = () => {
        window.location.reload()
    }

    // `category` is now just a plain name coming straight from the `category`
    // table — no color / rate_per_kg / km_factor attached to it. The KPI /
    // stage / ageing widgets below are still on the old Copper-PVC-Master
    // Batch mock dataset (dataByCategory) and haven't been migrated yet, so
    // they keep rendering the first mock dataset regardless of which real
    // category is selected until that migration happens.
    const active = dataByCategory[Object.keys(dataByCategory)[0]]
    // Placeholder cost/km factors until the KPI/stage/ageing widgets are
    // migrated off mock data — real values will come from wherever the
    // dashboard's actual inventory numbers end up living, not from `category`.
    const activeRate = 785
    const activeKmFactor = 4.14

    // ── Opening Balance (live, from backend) ─────────────────────────────
    // Recomputed whenever category, plant, costingType or the date range
    // changes. Backend matches productdetails.productCategory -> product
    // codes, sums goods_receipt qty/cost for those codes within the date
    // range, and prices it per the selected costing type.
    const [openingBalance, setOpeningBalance] = useState({ qty: 0, uom: null, cost: 0, uomMixed: false })
    const [openingBalanceLoading, setOpeningBalanceLoading] = useState(false)

    // ── Total Received (live, from backend) ──────────────────────────────
    // Same product-matching logic as Opening Balance, but summed across the
    // full selected date range instead of just the start date.
    const [totalReceived, setTotalReceived] = useState({ qty: 0, uom: null, cost: 0, uomMixed: false })
    const [totalReceivedLoading, setTotalReceivedLoading] = useState(false)
    const [totalReceivedPrev, setTotalReceivedPrev] = useState(null) // { qty, cost } for the prior equal-length period

    // ── Issued to Production (live, from backend) ────────────────────────
    // stock rows whose storage_locations.department_name = 'Production',
    // matched to this category's product codes, summed over the date range.
    const [issuedToProduction, setIssuedToProduction] = useState({ qty: 0, uom: null, cost: 0, uomMixed: false })
    const [issuedToProductionLoading, setIssuedToProductionLoading] = useState(false)
    const [issuedToProductionPrev, setIssuedToProductionPrev] = useState(null)

    // ── Scrap & Rework (live, from backend) ───────────────────────────────
    // confirmation_material_details_table rows matched to this category's
    // product codes, scrap qty = sum of all six scrap columns, over the date range.
    const [scrapAndRework, setScrapAndRework] = useState({ qty: 0, uom: null, cost: 0, uomMixed: false })
    const [scrapAndReworkLoading, setScrapAndReworkLoading] = useState(false)
    const [scrapAndReworkPrev, setScrapAndReworkPrev] = useState(null)

    // ── Inventory Movement Across Manufacturing Stages (live, from backend) ──
    // goods_issue_summary -> production_order_number -> confirmation_machine_parameters.workcenter_name
    // (the "stage"), In = confirm_qty, Out = confirmation_material_details.consumed_qty
    // for the same PO + batch_number, Bal = In - Out, over the date range.
    const [movementStages, setMovementStages] = useState({ stages: [], uom: null, uomMixed: false })
    const [movementStagesLoading, setMovementStagesLoading] = useState(false)

    // ── Inventory Ageing (live, from backend) ─────────────────────────────
    // No date range — buckets every on-hand `stock` row for this category's
    // products by how long it's been sitting (today - stock.created_at),
    // into 0–30 / 31–60 / 61–90 / >90 day buckets.
    const [ageingData, setAgeingData] = useState({ buckets: [], uom: null, uomMixed: false, totalQty: 0, totalCost: 0, note: null })
    const [ageingLoading, setAgeingLoading] = useState(false)

    // ── Inventory by Type (live, from backend) ────────────────────────────
    // No category selected -> top 5 categories on hand. Category selected ->
    // top 5 product codes within it.
    const [byTypeData, setByTypeData] = useState({ items: [], totalQty: 0, totalCost: 0 })
    const [byTypeLoading, setByTypeLoading] = useState(false)

    useEffect(() => {
        if (!category || !dateFrom || !dateTo) {
            setOpeningBalance({ qty: 0, uom: null, cost: 0, uomMixed: false })
            return
        }
        let cancelled = false
        setOpeningBalanceLoading(true)
        axios
            .get(`${PURCHASE_API}/inventory-dashboard/opening-balance`, {
                params: {
                    category,
                    plant,
                    costingType: costingType || 'moving_average',
                    from: dateFrom,
                    to: dateTo,
                },
            })
            .then((res) => {
                if (!cancelled) setOpeningBalance(res.data || { qty: 0, uom: null, cost: 0, uomMixed: false })
            })
            .catch((err) => {
                console.error('Failed to fetch opening balance:', err)
                if (!cancelled) setOpeningBalance({ qty: 0, uom: null, cost: 0, uomMixed: false })
            })
            .finally(() => {
                if (!cancelled) setOpeningBalanceLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType, dateFrom, dateTo])

    useEffect(() => {
        if (!category || !dateFrom || !dateTo) {
            setTotalReceived({ qty: 0, uom: null, cost: 0, uomMixed: false })
            setTotalReceivedPrev(null)
            return
        }
        let cancelled = false
        setTotalReceivedLoading(true)
        const { prevFrom, prevTo } = shiftRangeBack(dateFrom, dateTo)
        const base = { category, plant, costingType: costingType || 'moving_average' }
        Promise.all([
            axios.get(`${PURCHASE_API}/inventory-dashboard/total-received`, { params: { ...base, from: dateFrom, to: dateTo } }),
            axios.get(`${PURCHASE_API}/inventory-dashboard/total-received`, { params: { ...base, from: prevFrom, to: prevTo } }),
        ])
            .then(([curRes, prevRes]) => {
                if (cancelled) return
                setTotalReceived(curRes.data || { qty: 0, uom: null, cost: 0, uomMixed: false })
                setTotalReceivedPrev(prevRes.data || { qty: 0, cost: 0 })
            })
            .catch((err) => {
                console.error('Failed to fetch total received:', err)
                if (!cancelled) {
                    setTotalReceived({ qty: 0, uom: null, cost: 0, uomMixed: false })
                    setTotalReceivedPrev(null)
                }
            })
            .finally(() => {
                if (!cancelled) setTotalReceivedLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType, dateFrom, dateTo])

    useEffect(() => {
        if (!category || !dateFrom || !dateTo) {
            setIssuedToProduction({ qty: 0, uom: null, cost: 0, uomMixed: false })
            setIssuedToProductionPrev(null)
            return
        }
        let cancelled = false
        setIssuedToProductionLoading(true)
        const { prevFrom, prevTo } = shiftRangeBack(dateFrom, dateTo)
        const base = { category, plant, costingType: costingType || 'moving_average' }
        Promise.all([
            axios.get(`${PURCHASE_API}/inventory-dashboard/issued-to-production`, { params: { ...base, from: dateFrom, to: dateTo } }),
            axios.get(`${PURCHASE_API}/inventory-dashboard/issued-to-production`, { params: { ...base, from: prevFrom, to: prevTo } }),
        ])
            .then(([curRes, prevRes]) => {
                if (cancelled) return
                setIssuedToProduction(curRes.data || { qty: 0, uom: null, cost: 0, uomMixed: false })
                setIssuedToProductionPrev(prevRes.data || { qty: 0, cost: 0 })
            })
            .catch((err) => {
                console.error('Failed to fetch issued to production:', err)
                if (!cancelled) {
                    setIssuedToProduction({ qty: 0, uom: null, cost: 0, uomMixed: false })
                    setIssuedToProductionPrev(null)
                }
            })
            .finally(() => {
                if (!cancelled) setIssuedToProductionLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType, dateFrom, dateTo])

    useEffect(() => {
        if (!category || !dateFrom || !dateTo) {
            setScrapAndRework({ qty: 0, uom: null, cost: 0, uomMixed: false })
            setScrapAndReworkPrev(null)
            return
        }
        let cancelled = false
        setScrapAndReworkLoading(true)
        const { prevFrom, prevTo } = shiftRangeBack(dateFrom, dateTo)
        const base = { category, plant, costingType: costingType || 'moving_average' }
        Promise.all([
            axios.get(`${PURCHASE_API}/inventory-dashboard/scrap-and-rework`, { params: { ...base, from: dateFrom, to: dateTo } }),
            axios.get(`${PURCHASE_API}/inventory-dashboard/scrap-and-rework`, { params: { ...base, from: prevFrom, to: prevTo } }),
        ])
            .then(([curRes, prevRes]) => {
                if (cancelled) return
                setScrapAndRework(curRes.data || { qty: 0, uom: null, cost: 0, uomMixed: false })
                setScrapAndReworkPrev(prevRes.data || { qty: 0, cost: 0 })
            })
            .catch((err) => {
                console.error('Failed to fetch scrap and rework:', err)
                if (!cancelled) {
                    setScrapAndRework({ qty: 0, uom: null, cost: 0, uomMixed: false })
                    setScrapAndReworkPrev(null)
                }
            })
            .finally(() => {
                if (!cancelled) setScrapAndReworkLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType, dateFrom, dateTo])

    useEffect(() => {
        if (!category || !dateFrom || !dateTo) {
            setMovementStages({ stages: [], uom: null, uomMixed: false })
            return
        }
        let cancelled = false
        setMovementStagesLoading(true)
        axios
            .get(`${PURCHASE_API}/inventory-dashboard/movement-stages`, {
                params: {
                    category,
                    plant,
                    costingType: costingType || 'moving_average',
                    from: dateFrom,
                    to: dateTo,
                },
            })
            .then((res) => {
                if (!cancelled) setMovementStages(res.data || { stages: [], uom: null, uomMixed: false })
            })
            .catch((err) => {
                console.error('Failed to fetch inventory movement stages:', err)
                if (!cancelled) setMovementStages({ stages: [], uom: null, uomMixed: false })
            })
            .finally(() => {
                if (!cancelled) setMovementStagesLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType, dateFrom, dateTo])

    useEffect(() => {
        if (!category) {
            setAgeingData({ buckets: [], uom: null, uomMixed: false, totalQty: 0, totalCost: 0, note: null })
            return
        }
        let cancelled = false
        setAgeingLoading(true)
        axios
            .get(`${PURCHASE_API}/inventory-dashboard/ageing`, {
                params: { category, plant, costingType: costingType || 'moving_average' },
            })
            .then((res) => {
                if (!cancelled) setAgeingData(res.data || { buckets: [], uom: null, uomMixed: false, totalQty: 0, totalCost: 0, note: null })
            })
            .catch((err) => {
                console.error('Failed to fetch inventory ageing:', err)
                if (!cancelled) setAgeingData({ buckets: [], uom: null, uomMixed: false, totalQty: 0, totalCost: 0, note: null })
            })
            .finally(() => {
                if (!cancelled) setAgeingLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, plant, costingType])

    useEffect(() => {
        let cancelled = false
        setByTypeLoading(true)
        axios
            .get(`${PURCHASE_API}/inventory-dashboard/by-type`, {
                params: { category, costingType: costingType || 'moving_average' },
            })
            .then((res) => {
                if (!cancelled) setByTypeData(res.data || { items: [], totalQty: 0, totalCost: 0 })
            })
            .catch((err) => {
                console.error('Failed to fetch inventory by type:', err)
                if (!cancelled) setByTypeData({ items: [], totalQty: 0, totalCost: 0 })
            })
            .finally(() => {
                if (!cancelled) setByTypeLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [category, costingType])

    // ── On Hand Balance = Total Received − Issued to Production ──────────
    // Purely derived from the two live KPIs above — no extra backend call.
    const onHandBalance = {
        qty: (totalReceived.qty || 0) - (issuedToProduction.qty || 0),
        cost: (totalReceived.cost || 0) - (issuedToProduction.cost || 0),
        uom: totalReceived.uom || issuedToProduction.uom || null,
        uomMixed: totalReceived.uomMixed || issuedToProduction.uomMixed,
    }
    const onHandBalanceLoading = totalReceivedLoading || issuedToProductionLoading
    const onHandBalanceTrend =
        totalReceivedPrev && issuedToProductionPrev
            ? calcTrend(onHandBalance.qty, (totalReceivedPrev.qty || 0) - (issuedToProductionPrev.qty || 0))
            : null
    const totalReceivedTrend = totalReceivedPrev ? calcTrend(totalReceived.qty, totalReceivedPrev.qty) : null
    const issuedToProductionTrend = issuedToProductionPrev ? calcTrend(issuedToProduction.qty, issuedToProductionPrev.qty) : null
    const scrapAndReworkTrend = scrapAndReworkPrev ? calcTrend(scrapAndRework.qty, scrapAndReworkPrev.qty) : null

    const categoryOptions = [
        { value: '', label: 'Select' },
        ...materialCategories.map((m) => ({ value: m.name, label: m.name, id: m.id })),
    ]
    const categorySelectValue = category ? { value: category, label: category } : { value: '', label: 'Select' }

    return (
        <Layout defaultSidebarOpen={false}>
            <div style={{ background: '#F8FAFC', minHeight: '100vh', padding: 24, fontFamily: "'Inter', 'Segoe UI', sans-serif" }}>
                {/* Header */}
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 20 }}>
                    <div>
                        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#0F172A', margin: 0 }}>Inventory Dashboard</h1>
                        <p style={{ fontSize: 14, color: '#64748B', margin: '4px 0 0' }}>Track material flow across the cable manufacturing process</p>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 16, borderRadius: 10, padding: 12 }}>
                        <FilterField label="Material Category">
                            <Select
                                isClearable={false}
                                isSearchable
                                isLoading={categoryLoading}
                                placeholder="Select Category"
                                value={categorySelectValue}
                                options={categoryOptions}
                                onChange={(opt) => setCategory(opt ? opt.value : '')}
                                menuPortalTarget={document.body}
                                maxMenuHeight={200}
                                styles={{
                                    container: (base) => ({ ...base, width: 190 }),
                                    control: (base) => ({ ...base, minHeight: 36, fontSize: 14 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                    menu: (base) => ({ ...base, fontSize: 13 }),
                                    option: (base) => ({ ...base, fontSize: 13 }),
                                }}
                            />
                        </FilterField>
                        <FilterField label="Plant">
                            <Select
                                isDisabled
                                isSearchable={false}
                                placeholder="Select"
                                value={null}
                                options={[]}
                                onChange={() => {}}
                                menuPortalTarget={document.body}
                                styles={{
                                    container: (base) => ({ ...base, width: 160 }),
                                    control: (base) => ({ ...base, minHeight: 36, fontSize: 14, cursor: 'not-allowed' }),
                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                }}
                            />
                        </FilterField>
                        <FilterField label="Costing Type">
                            <Select
                                isClearable={false}
                                isSearchable={false}
                                placeholder="Select"
                                value={costingTypeSelectValue}
                                options={costingTypeOptions}
                                onChange={(opt) => setCostingType(opt ? opt.value : '')}
                                menuPortalTarget={document.body}
                                styles={{
                                    container: (base) => ({ ...base, width: 170 }),
                                    control: (base) => ({ ...base, minHeight: 36, fontSize: 14 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
                                    menu: (base) => ({ ...base, fontSize: 13 }),
                                    option: (base) => ({ ...base, fontSize: 13 }),
                                }}
                            />
                        </FilterField>
                        <FilterField label="Date Range">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #E2E8F0', borderRadius: 8, padding: '6px 10px', background: '#fff' }}>
                                <Icon.Calendar width={14} height={14} style={{ color: '#94A3B8', flexShrink: 0 }} />
                                <input
                                    type="date"
                                    value={dateFrom}
                                    max={dateTo}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    style={{ border: 'none', outline: 'none', fontSize: 13.5, color: '#334155', fontFamily: 'inherit' }}
                                />
                                <span style={{ color: '#94A3B8' }}>–</span>
                                <input
                                    type="date"
                                    value={dateTo}
                                    min={dateFrom}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    style={{ border: 'none', outline: 'none', fontSize: 13.5, color: '#334155', fontFamily: 'inherit' }}
                                />
                            </div>
                        </FilterField>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 4 }}>
                        <button onClick={handleRefresh} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#475569', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px', background: '#fff', cursor: 'pointer' }}>
                            <Icon.Refresh width={14} height={14} /> Refresh
                        </button>
                        <button style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#475569', border: '1px solid #E2E8F0', borderRadius: 8, padding: '8px 12px', background: '#fff', cursor: 'pointer' }}>
                            <Icon.Export width={14} height={14} /> Export
                        </button>
                    </div>
                </div>

                {/* KPI row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                    {active.kpis.map((k) => {
                        if (k.label === 'Opening Balance') {
                            return (
                                <LiveMetricCard
                                    key={k.label}
                                    label="Opening Balance"
                                    icon={k.icon}
                                    iconBg={k.iconBg}
                                    iconColor={k.iconColor}
                                    data={openingBalance}
                                    loading={openingBalanceLoading}
                                />
                            )
                        }
                        if (k.label === 'Total Received') {
                            return (
                                <LiveMetricCard
                                    key={k.label}
                                    label="Total Received"
                                    icon={k.icon}
                                    iconBg={k.iconBg}
                                    iconColor={k.iconColor}
                                    data={totalReceived}
                                    loading={totalReceivedLoading}
                                    trend={totalReceivedTrend}
                                />
                            )
                        }
                        if (k.label === 'Issued to Production') {
                            return (
                                <LiveMetricCard
                                    key={k.label}
                                    label="Issued to Production"
                                    icon={k.icon}
                                    iconBg={k.iconBg}
                                    iconColor={k.iconColor}
                                    data={issuedToProduction}
                                    loading={issuedToProductionLoading}
                                    trend={issuedToProductionTrend}
                                />
                            )
                        }
                        if (k.label === 'On Hand Balance') {
                            return (
                                <LiveMetricCard
                                    key={k.label}
                                    label="On Hand Balance"
                                    icon={k.icon}
                                    iconBg={k.iconBg}
                                    iconColor={k.iconColor}
                                    data={onHandBalance}
                                    loading={onHandBalanceLoading}
                                    trend={onHandBalanceTrend}
                                />
                            )
                        }
                        if (k.label === 'Scrap & Rework') {
                            return (
                                <LiveMetricCard
                                    key={k.label}
                                    label="Scrap & Rework"
                                    icon={k.icon}
                                    iconBg={k.iconBg}
                                    iconColor={k.iconColor}
                                    data={scrapAndRework}
                                    loading={scrapAndReworkLoading}
                                    trend={scrapAndReworkTrend}
                                />
                            )
                        }
                        return <KpiCard key={k.label} item={k} ratePerKg={activeRate} />
                    })}
                </div>

                {/* Info strip */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: '#EFF6FF', border: '1px solid #DBEAFE', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
                    <Icon.Swap width={16} height={16} style={{ color: '#3B82F6', marginTop: 2, flexShrink: 0 }} />
                    <div style={{ fontSize: 14 }}>
                        <p style={{ color: '#334155', fontWeight: 500, margin: 0 }}>Measurements shown in both Kilometres (km) and Kilograms (kg)</p>
                        <p style={{ color: '#94A3B8', fontSize: 12, margin: '2px 0 0' }}>Conversion based on product master (kg per km)</p>
                    </div>
                </div>

                {/* Stage flow */}
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 20, marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 600, color: '#0F172A', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                            Inventory Movement Across Manufacturing Stages <span style={{ color: '#94A3B8', fontWeight: 400 }}>({category})</span>
                            <Icon.Info width={14} height={14} style={{ color: '#CBD5E1' }} />
                        </h2>
                        <button style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#2563EB', border: '1px solid #BFDBFE', borderRadius: 8, padding: '6px 12px', background: '#fff', cursor: 'pointer' }}>
                            <Icon.List width={14} height={14} /> View Stage List ({movementStages.stages.length}+ Stages)
                        </button>
                    </div>
                    {movementStages.uomMixed && (
                        <p style={{ fontSize: 11, color: '#C2410C', margin: '0 0 8px' }}>Mixed UOM across products in this category</p>
                    )}
                    {movementStagesLoading ? (
                        <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>Loading stage flow…</p>
                    ) : movementStages.stages.length === 0 ? (
                        <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>
                            No production activity in this date range for {category || 'the selected category'}.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', overflowX: 'auto', paddingBottom: 8 }}>
                            {movementStages.stages.map((s, i) => (
                                <StageCard
                                    key={s.name + i}
                                    stage={s}
                                    isLast={i === movementStages.stages.length - 1}
                                    color={STAGE_COLORS[i % STAGE_COLORS.length]}
                                    uom={movementStages.uom}
                                />
                            ))}
                            <button style={{ marginLeft: 8, width: 28, height: 28, borderRadius: '50%', border: '1px solid #E2E8F0', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#94A3B8', cursor: 'pointer' }}>
                                <Icon.ChevronRight width={16} height={16} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Bottom row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
                    {/* Inventory by Type */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', margin: 0 }}>
                                Inventory by Type <span style={{ color: '#94A3B8', fontWeight: 400 }}>(On Hand{category ? ` · Top 5 in ${category}` : ' · Top 5 Categories'})</span>
                            </h3>
                        </div>
                        {byTypeLoading ? (
                            <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>Loading…</p>
                        ) : byTypeData.items.length === 0 ? (
                            <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>No on-hand stock found.</p>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                                <DonutChart
                                    data={byTypeData.items.map((d, i) => ({ ...d, color: TYPE_COLORS[i % TYPE_COLORS.length], pctRaw: d.pct }))}
                                    total={formatStageQty(byTypeData.totalQty, byTypeData.items[0]?.uomMixed ? null : byTypeData.items[0]?.uom)}
                                    totalLabel="On Hand"
                                />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13.5, flex: 1, minWidth: 0 }}>
                                    {byTypeData.items.map((d, i) => (
                                        <div key={d.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', minWidth: 0 }}>
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: TYPE_COLORS[i % TYPE_COLORS.length], display: 'inline-block', flexShrink: 0 }} />
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
                                            </span>
                                            <span style={{ color: '#1E293B', fontWeight: 600, flexShrink: 0 }}>{formatStageQty(d.qty, d.uom)}</span>
                                            <span style={{ color: '#0F766E', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>{formatCurrencyValue(d.cost)}</span>
                                            <span style={{ color: '#94A3B8', fontSize: 12, width: 44, textAlign: 'right', flexShrink: 0 }}>{d.pct.toFixed(1)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Inventory Ageing */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', margin: 0 }}>Inventory Ageing <span style={{ color: '#94A3B8', fontWeight: 400 }}>({category})</span></h3>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94A3B8', marginBottom: 10 }}>
                            <span>Inventory Type</span>
                            <span>{ageingData.uom ? `Qty (${ageingData.uom})` : 'Qty'}</span>
                        </div>
                        {ageingData.uomMixed && (
                            <p style={{ fontSize: 11, color: '#C2410C', margin: '0 0 10px' }}>Mixed UOM across products in this category</p>
                        )}
                        {ageingLoading ? (
                            <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>Loading ageing…</p>
                        ) : ageingData.buckets.length === 0 || ageingData.totalQty === 0 ? (
                            <p style={{ fontSize: 13, color: '#94A3B8', margin: 0, padding: '12px 0' }}>
                                No on-hand stock found for {category || 'the selected category'}.
                            </p>
                        ) : (
                            <>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {ageingData.buckets.map((b, i) => (
                                        <div key={b.label}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13.5, marginBottom: 2, gap: 6 }}>
                                                <span style={{ color: '#475569' }}>{b.label}</span>
                                                <span style={{ color: '#1E293B', fontWeight: 600 }}>{formatStageQty(b.qty, ageingData.uom)}</span>
                                                <span style={{ color: '#94A3B8', fontSize: 12, width: 44, textAlign: 'right' }}>{b.pct.toFixed(1)}%</span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#0F766E', fontWeight: 600, marginBottom: 5, textAlign: 'right' }}>{formatCurrencyValue(b.cost)}</div>
                                            <div style={{ height: 8, borderRadius: 999, background: '#F1F5F9', overflow: 'hidden' }}>
                                                <div style={{ height: '100%', borderRadius: 999, width: `${b.pct}%`, background: AGEING_BUCKET_COLORS[i % AGEING_BUCKET_COLORS.length] }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: '#64748B', borderTop: '1px solid #F1F5F9', paddingTop: 10 }}>
                                    <span>Total On Hand</span>
                                    <span style={{ color: '#0F172A', fontWeight: 700 }}>{formatStageQty(ageingData.totalQty, ageingData.uom)} · {formatCurrencyValue(ageingData.totalCost)}</span>
                                </div>
                                {ageingData.note && (
                                    <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 8, background: '#FFF7ED', border: '1px solid #FFEDD5', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#C2410C' }}>
                                        <Icon.Alert width={14} height={14} style={{ marginTop: 2, flexShrink: 0 }} />
                                        {ageingData.note}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Alerts & Exceptions */}
                    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: 20 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#0F172A', margin: 0 }}>Alerts &amp; Exceptions</h3>
                            <button style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12.5, color: '#2563EB', background: 'none', border: 'none', cursor: 'pointer' }}>
                                View All Alerts <Icon.ArrowRight width={12} height={12} />
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                            {alerts.map((a, i) => {
                                const s = levelStyles[a.level]
                                return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, fontSize: 13.5 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: s.bg, color: s.color, flexShrink: 0 }}>
                                            {a.level}
                                        </span>
                                        <span style={{ color: '#475569', flex: 1 }}>{a.msg}</span>
                                        <span style={{ color: '#1E293B', fontWeight: 600, flexShrink: 0 }}>{a.qty}</span>
                                        <span style={{ color: '#0F766E', fontSize: 12, fontWeight: 600, flexShrink: 0, width: 66, textAlign: 'right' }}>{costFromKg(a.qty, a.ratePerKg)}</span>
                                        <span style={{ color: '#94A3B8', fontSize: 12, flexShrink: 0, width: 44, textAlign: 'right' }}>{a.date}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Footer note */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, color: '#94A3B8', paddingTop: 20 }}>
                    <Icon.Info width={12} height={12} />
                    All quantities are shown in both Kilograms (kg) and Kilometres (km)
                    <span style={{ margin: '0 4px' }}>|</span>
                    Data is real-time and synchronized across all plants
                </div>
            </div>
        </Layout>
    )
}

export default InventoryDashboard